import { useEffect, useRef, useState, type CSSProperties } from "react";
import { ArrowIcon } from "../../components/ArrowIcon";
import { InstallCommand } from "./InstallCommand";
import "./ContCamPage.css";
import "./OmaWisperPage.css";

const sections = [
  ["overview", "Overview"],
  ["dictation", "Speak, then paste"],
  ["transcription", "Transcription"],
  ["install", "Installation"],
  ["shortcuts", "Shortcuts & commands"],
  ["notes", "A few notes"],
] as const;

function MicrophoneIcon() {
  return <svg width="15" height="15" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" aria-hidden="true"><rect x="7" y="2" width="6" height="10" rx="3" /><path d="M4 9a6 6 0 0 0 12 0M10 15v3m-3 0h6" /></svg>;
}

function DictationDemo() {
  const [phase, setPhase] = useState<"idle" | "listening" | "transcribing" | "done">("idle");
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => () => { if (timer.current) clearTimeout(timer.current); }, []);

  const finish = () => {
    if (timer.current) clearTimeout(timer.current);
    setPhase("transcribing");
    timer.current = setTimeout(() => setPhase("done"), 650);
  };
  const start = () => {
    if (timer.current) clearTimeout(timer.current);
    setPhase("listening");
    timer.current = setTimeout(finish, 2400);
  };
  const cancel = () => {
    if (timer.current) clearTimeout(timer.current);
    setPhase("idle");
  };
  const busy = phase === "listening" || phase === "transcribing";

  return <figure className="ow-demo-figure">
    <div className="ow-demo" data-phase={phase}>
      <div className="ow-demo-bar">
        <span className="ow-desktop-name">Omarchy</span>
        <div className="ow-demo-indicator" aria-hidden="true">
          {phase === "listening" ? <span className="ow-waveform">{[4, 9, 16, 10, 20, 13, 6, 15, 8].map((height, index) => <i key={index} style={{ "--bar-height": `${height}px`, "--bar-delay": `${index * -0.12}s` } as CSSProperties} />)}</span> : <span>{phase === "transcribing" ? "Transcribing…" : phase === "done" ? "Pasted" : "Ready"}</span>}
        </div>
        <button className="ow-mic-button" type="button" onClick={phase === "listening" ? finish : start} disabled={phase === "transcribing"} aria-label={phase === "listening" ? "Finish example dictation" : phase === "transcribing" ? "Transcribing example" : phase === "done" ? "Replay dictation example" : "Start dictation example"}>
          <MicrophoneIcon /><span>{phase === "listening" ? "Finish" : phase === "transcribing" ? "Working…" : phase === "done" ? "Replay" : "Try it"}</span>
        </button>
      </div>
      <div className="ow-note">
        <span className="ow-note-label">A note to the team</span>
        <p className={phase === "done" ? "ow-transcript" : "ow-placeholder"}>{phase === "done" ? "The update looks good. I’ll send the final version this afternoon." : "Your words will appear here."}</p>
        <div className="ow-demo-status">
          <span role="status">{phase === "listening" ? "Example dictation in progress…" : phase === "transcribing" ? "Turning speech into text…" : phase === "done" ? "Sample text pasted into the note." : "Try the microphone button above."}</span>
          {busy && <button type="button" onClick={cancel}>Cancel example</button>}
        </div>
      </div>
    </div>
    <figcaption>Interactive illustration with sample text. This page doesn’t access your microphone.</figcaption>
  </figure>;
}

export default function OmaWisperPage() {
  const [activeSection, setActiveSection] = useState("overview");
  useEffect(() => {
    document.title = "OmaWisper — Tools by Yash Bindal";
    document.querySelector('meta[name="description"]')?.setAttribute("content", "Voice dictation for Omarchy, by Yash Bindal. Speak, transcribe, and paste into your focused app from a shortcut or the top bar.");
    const updateSection = () => {
      let current: string = "overview";
      for (const [id] of sections) {
        if ((document.getElementById(id)?.getBoundingClientRect().top ?? Infinity) <= 180) current = id;
      }
      setActiveSection(current);
    };
    window.addEventListener("scroll", updateSection, { passive: true });
    updateSection();
    if (window.location.hash) requestAnimationFrame(() => document.getElementById(window.location.hash.slice(1))?.scrollIntoView());
    return () => window.removeEventListener("scroll", updateSection);
  }, []);

  return <main className="omacam-page omawisper-page">
    <a className="oc-skip" href="#overview">Skip to OmaWisper</a>
    <aside className="oc-rail">
      <a className="oc-back" href="/tools"><ArrowIcon direction="left" /><span>Tools</span></a>
      <nav aria-label="On this page">{sections.map(([id, label]) => <a key={id} href={`#${id}`} aria-current={activeSection === id ? "location" : undefined}>{label}</a>)}</nav>
      <a className="oc-author" href="/"><span>Yash Bindal</span><ArrowIcon direction="upRight" /></a>
    </aside>

    <article className="oc-article">
      <header id="overview" className="oc-intro">
        <h1>OmaWisper</h1>
        <p className="oc-byline">A tool by <a href="/">Yash Bindal</a> <span aria-hidden="true">·</span> Voice dictation for Omarchy</p>
        <p>Sometimes it’s easier to say it. OmaWisper turns your speech into text and pastes it into the app you’re using.</p>
        <p>Start from a shortcut or the microphone in your top bar. Speak, stop, and carry on writing.</p>
        <a className="oc-install-link" href="#install"><span>Install OmaWisper</span><ArrowIcon direction="down" /></a>
      </header>

      <DictationDemo />

      <section id="dictation">
        <h2>Speak, then paste</h2>
        <p>Click the microphone to start recording. Click again to transcribe. The text goes into your focused app, whether you’re writing a message, a note, or something longer.</p>
        <p>The bar shows when OmaWisper is listening and when it’s transcribing. You can discard a recording before it’s sent.</p>
        <dl className="ow-controls-list">
          <div><dt>Left click</dt><dd>Start or finish dictation</dd></div>
          <div><dt>Right click</dt><dd>Stop and transcribe</dd></div>
          <div><dt>Middle click</dt><dd>Discard the recording</dd></div>
        </dl>
      </section>

      <section id="transcription">
        <h2>Choose how to transcribe</h2>
        <p>OmaWisper supports two transcription backends. Where your audio goes depends on which one you configure.</p>
        <h3>Groq</h3>
        <p>The default option sends your recorded audio to Groq for transcription. Bring your own API key; Groq’s usage limits and any charges apply.</p>
        <h3>Local Parakeet</h3>
        <p>Connect OmaWisper to a Parakeet server running on your computer. You’ll need to set up that server separately—the installer doesn’t include a model.</p>
        <p className="ow-config-note">Choose the backend in <code>~/.config/omawisper/config.toml</code>.</p>
      </section>

      <section id="install">
        <h2>Installation</h2>
        <p className="oc-install-lead"><mark className="oc-marker">One command.</mark> Your voice, on Omarchy.</p>
        <p>Run this in your desktop terminal as your normal user. The installer builds OmaWisper, adds the top-bar widget, and sets up its background service.</p>
        <InstallCommand tool="omawisper" />
        <p>For Groq, enter your API key at the terminal prompt. You can skip it if you’re setting up local Parakeet. Installation doesn’t start recording.</p>
        <p>Then run <code className="ow-inline-code">omawisper doctor</code> to check your configuration and desktop tools. Click the microphone when you’re ready to dictate.</p>
        <details className="ow-details">
          <summary>Upgrading from WisprRust or Wisper?</summary>
          <p>Finish your current recording first. Copy your existing configuration and credentials into <code>~/.config/omawisper/</code>, keeping the originals. Stop and disable the old service before running the new installer, then update your shortcuts to use <code>omawisper</code>.</p>
          <p>Keep the old configuration until you’ve tested dictation. The installer stops if an earlier WisprRust or Wisper service is still running.</p>
        </details>
      </section>

      <section id="shortcuts">
        <h2>Shortcuts & commands</h2>
        <p>Bind an unused keyboard shortcut to <code className="ow-inline-code">omawisper toggle</code>. Press it to start recording, then again to transcribe and paste. Keep the app you want to write into focused.</p>
        <dl className="ow-controls-list ow-commands">
          <div><dt><code>omawisper toggle</code></dt><dd>Start or finish</dd></div>
          <div><dt><code>omawisper cancel</code></dt><dd>Discard recording</dd></div>
          <div><dt><code>omawisper status</code></dt><dd>Check the daemon</dd></div>
          <div><dt><code>omawisper doctor</code></dt><dd>Check your setup</dd></div>
        </dl>
        <p>The installer leaves your existing keyboard shortcuts alone.</p>
      </section>

      <section id="notes">
        <h2>A few notes</h2>
        <ul className="oc-notes">
          <li>The app is free. Transcription providers have their own limits and pricing.</li>
          <li>Built for Omarchy and Wayland. Older Waybar setups have a separate module configuration.</li>
          <li>OmaWisper was previously called WisprRust, then briefly Wisper. The current app and command are <code>omawisper</code>.</li>
        </ul>
        <p>OmaWisper and <a className="oc-inline-link" href="/tools/contcam">ContCam</a> are part of OMA: small tools I build for my desktop.</p>
      </section>

      <footer className="oc-footer"><a href="/tools"><ArrowIcon direction="left" /><span>All tools</span></a><a href="https://github.com/omayb/omawisper" target="_blank" rel="noopener noreferrer"><span>GitHub</span><ArrowIcon direction="upRight" /></a></footer>
    </article>
  </main>;
}
